import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, Droplets, ChevronDown, ChevronUp, X, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import fetchWithAuth from '../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

/* ------------------------------------------------------------------ */
/* Single Treatment Row — active treatment, removable                  */
/* ------------------------------------------------------------------ */
const TreatmentRow = ({ rule, idx, bomId, revisionId, lineId, onRefresh, isDraft }) => {
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      const tk = getToken();
      const res = await fetchWithAuth(
        `${API}/api/v1/bom/${bomId}/revisions/${revisionId}/lines/${lineId}/process-rules/${idx}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${tk}` } }
      );
      if (res.ok) { toast.success('Treatment removed'); onRefresh(); }
      else { const e = await res.json(); toast.error(e.detail || 'Remove failed'); }
    } catch { toast.error('Remove failed'); }
    setRemoving(false);
  };

  return (
    <div className="group flex items-center gap-2 py-1.5 px-2 rounded-md transition-colors bg-violet-900/15 border border-violet-700/30"
      data-testid={`treatment-row-${idx}`}>
      <div className="w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] text-slate-200 truncate">{rule.rule_name}</div>
        <div className="text-[9px] text-slate-500">
          {rule.rule_code && <span>{rule.rule_code} | </span>}
          {rule.quantity > 0 && <span>{rule.quantity} {rule.uom}</span>}
          {rule.specification_ref && <span className="text-cyan-600/60"> | {rule.specification_ref}</span>}
        </div>
      </div>

      <span className="font-mono text-[11px] shrink-0 text-emerald-400 font-semibold" data-testid={`treatment-cost-${idx}`}>
        {rule.cost?.toLocaleString()}
      </span>

      {isDraft && (
        <button onClick={handleRemove} disabled={removing}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500/60 hover:text-red-400 p-0.5"
          data-testid={`treatment-remove-${idx}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Inline Create Surface Treatment — Creates in Process Library        */
/* ------------------------------------------------------------------ */
const InlineCreateTreatment = ({ onCreated, onClose }) => {
  const [form, setForm] = useState({
    process_name: '', process_category: 'Surface Preparation',
    cost_rate: '', unit_label: 'm2', standard_ref: '',
  });
  const [saving, setSaving] = useState(false);

  const CATEGORIES = [
    'Surface Preparation', 'Metallic Coatings', 'Paint System',
    'Chemical Treatment', 'Powder/Specialty',
  ];

  const handleSubmit = async () => {
    if (!form.process_name.trim()) { toast.error('Treatment name is required'); return; }
    setSaving(true);
    const slug = form.process_name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '-').slice(0, 20);
    const code = `SFT-CUSTOM-${slug}`;
    try {
      const tk = getToken();
      const body = {
        process_code: code,
        process_name: form.process_name.trim(),
        process_domain: 'surface_treatment',
        process_category: form.process_category,
        status: 'active',
        definition: { standard_ref: form.standard_ref || '' },
        cost_model: {
          model_type: 'per_area',
          rate: parseFloat(form.cost_rate) || 0,
          unit_label: form.unit_label || 'm2',
          currency: 'INR',
        },
        origin: 'user_created',
      };
      const res = await fetchWithAuth(`${API}/api/v1/process-library/processes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const created = await res.json();
        toast.success(`Treatment "${form.process_name}" created in Process Library`);
        onCreated?.(created);
        onClose();
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Create failed');
      }
    } catch { toast.error('Create failed'); }
    setSaving(false);
  };

  return (
    <div className="border border-violet-600/40 rounded-lg bg-slate-900 p-3 mt-2 shadow-xl animate-in fade-in duration-150"
      data-testid="inline-create-treatment-form">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-violet-400 font-semibold uppercase tracking-wider">New Surface Treatment</span>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="space-y-2">
        <Input value={form.process_name} onChange={e => setForm(f => ({ ...f, process_name: e.target.value }))}
          placeholder="Treatment name *" className="h-7 text-[11px] bg-slate-800 border-slate-600 text-white"
          data-testid="inline-treatment-name" autoFocus />
        <select value={form.process_category} onChange={e => setForm(f => ({ ...f, process_category: e.target.value }))}
          className="h-7 w-full text-[11px] bg-slate-800 border border-slate-600 text-white rounded px-2"
          data-testid="inline-treatment-category">
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="flex gap-2">
          <Input type="number" value={form.cost_rate} onChange={e => setForm(f => ({ ...f, cost_rate: e.target.value }))}
            placeholder="Rate" className="h-7 flex-1 text-[10px] bg-slate-800 border-slate-600 text-white font-mono" min="0" />
          <select value={form.unit_label} onChange={e => setForm(f => ({ ...f, unit_label: e.target.value }))}
            className="h-7 w-20 text-[10px] bg-slate-800 border border-slate-600 text-white rounded px-1">
            <option value="m2">/m2</option>
            <option value="piece">/piece</option>
            <option value="kg">/kg</option>
            <option value="litre">/litre</option>
          </select>
        </div>
        <Input value={form.standard_ref} onChange={e => setForm(f => ({ ...f, standard_ref: e.target.value }))}
          placeholder="Specification ref (optional)" className="h-7 text-[10px] bg-slate-800 border-slate-600 text-white" />
        <div className="flex gap-2">
          <Button size="sm" className="flex-1 h-6 text-[10px] bg-violet-600 hover:bg-violet-500 text-white" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Creating...' : 'Create Treatment'}
          </Button>
          <Button size="sm" variant="outline" className="h-6 text-[10px] border-slate-600 text-slate-300" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Add Treatment Popover — Powered by Process Library                  */
/* Source: /api/v1/process-library/processes?domain=surface_treatment   */
/* Apply: /api/v1/process-library/apply-to-line                        */
/* ------------------------------------------------------------------ */
const AddTreatmentPopover = ({ bomId, revisionId, lineId, activeRuleCodes, onRefresh, onClose, onBusy }) => {
  const [processes, setProcesses] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(new Map()); // process_code → process object
  const inputRef = useRef(null);

  const fetchProcesses = async () => {
    const tk = getToken();
    try {
      const res = await fetchWithAuth(
        `${API}/api/v1/process-library/processes?domain=surface_treatment&status=active&limit=200`,
        { headers: { Authorization: `Bearer ${tk}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setProcesses(data.items || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => {
    fetchProcesses();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const filtered = processes.filter(p =>
    !activeRuleCodes.has(p.process_code) &&
    (p.process_name?.toLowerCase().includes(search.toLowerCase()) ||
     p.process_code?.toLowerCase().includes(search.toLowerCase()) ||
     p.process_category?.toLowerCase().includes(search.toLowerCase()))
  );

  const grouped = {};
  filtered.forEach(p => {
    const cat = p.process_category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(p);
  });

  const toggleSelect = (p) => {
    setSelected(prev => {
      const next = new Map(prev);
      if (next.has(p.process_code)) next.delete(p.process_code);
      else next.set(p.process_code, p);
      return next;
    });
  };

  const applySelected = async () => {
    if (selected.size === 0) return;
    setApplying(true);
    onBusy?.('applying');
    const tk = getToken();
    try {
      const res = await fetchWithAuth(`${API}/api/v1/process-library/batch-apply-processes-to-line`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({
          process_codes: [...selected.keys()],
          bom_id: bomId,
          revision_id: revisionId,
          line_id: lineId,
        }),
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Applied ${result.applied} treatment${result.applied !== 1 ? 's' : ''}`);
        onRefresh(); onClose();
      } else { const e = await res.json(); toast.error(e.detail || 'Apply failed'); }
    } catch { toast.error('Apply failed'); }
    setApplying(false);
    onBusy?.(null);
  };

  const costDisplay = (p) => {
    const cm = p.cost_model || {};
    const rate = cm.rate || 0;
    const label = cm.unit_label || 'unit';
    return rate > 0 ? `${rate}/${label}` : '';
  };

  if (showCreate) {
    return (
      <InlineCreateTreatment
        onCreated={(created) => { setProcesses(prev => [...prev, created]); setShowCreate(false); }}
        onClose={() => setShowCreate(false)}
      />
    );
  }

  return (
    <div className="border border-violet-600/40 rounded-lg bg-slate-900 p-3 mt-2 shadow-xl" data-testid="treatment-add-popover">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] text-violet-400 font-semibold uppercase tracking-wider">Add Treatment</span>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300"><X className="w-3.5 h-3.5" /></button>
      </div>

      <div className="relative mb-2">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
        <Input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search treatments..."
          className="h-7 text-[11px] bg-slate-800 border-slate-600 text-white pl-7"
          data-testid="treatment-search" />
      </div>

      <div className="max-h-[200px] overflow-y-auto space-y-1">
        {loading ? (
          <div className="text-[10px] text-slate-500 text-center py-4">Loading from Process Library...</div>
        ) : (
          <>
            {Object.keys(grouped).length === 0 ? (
              <div className="text-[10px] text-slate-500 text-center py-3">No matching treatments</div>
            ) : (
              Object.entries(grouped).map(([cat, items]) => (
                <div key={cat}>
                  <div className="text-[9px] text-violet-400/70 font-semibold uppercase tracking-wider px-1 py-0.5 sticky top-0 bg-slate-900">
                    {cat}
                  </div>
                  {items.map(p => {
                    const isChecked = selected.has(p.process_code);
                    return (
                      <button key={p.process_code} onClick={() => toggleSelect(p)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                          isChecked ? 'bg-violet-900/40 border border-violet-600/40' : 'hover:bg-violet-900/20 border border-transparent'
                        }`}
                        data-testid={`treatment-add-${p.process_code}`}>
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors ${
                          isChecked ? 'bg-violet-600 border-violet-500' : 'border-slate-600 bg-slate-800'
                        }`}>
                          {isChecked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] text-slate-200 truncate">{p.process_name}</div>
                          <div className="text-[9px] text-slate-500">{p.process_code}</div>
                        </div>
                        <span className="text-[10px] text-violet-400/70 font-mono shrink-0 ml-2">{costDisplay(p)}</span>
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </>
        )}
      </div>

      {/* Selection Summary */}
      {selected.size > 0 && (
        <div className="mt-2 border border-violet-600/30 rounded-md bg-violet-950/30 p-2" data-testid="treatment-selection-summary">
          <div className="text-[9px] text-violet-400 font-semibold uppercase tracking-wider mb-1">
            Selected ({selected.size})
          </div>
          <div className="space-y-0.5 max-h-[80px] overflow-y-auto">
            {[...selected.values()].map(p => (
              <div key={p.process_code} className="flex items-center justify-between text-[10px]">
                <span className="text-slate-300 truncate">{p.process_name}</span>
                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                  <span className="text-violet-400/70 font-mono">{costDisplay(p)}</span>
                  <button onClick={(e) => { e.stopPropagation(); toggleSelect(p); }}
                    className="text-slate-600 hover:text-red-400 transition-colors" data-testid={`treatment-deselect-${p.process_code}`}>
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Apply Button */}
      {selected.size > 0 && (
        <Button size="sm" onClick={applySelected} disabled={applying}
          className="w-full mt-2 h-7 text-[10px] bg-violet-600 hover:bg-violet-500 text-white font-semibold"
          data-testid="treatment-apply-selected-btn">
          {applying ? 'Applying...' : `Apply ${selected.size} Treatment${selected.size !== 1 ? 's' : ''}`}
        </Button>
      )}

      <button onClick={() => setShowCreate(true)}
        className="w-full mt-2 py-1.5 text-[10px] text-violet-400 hover:text-violet-300 border border-dashed border-violet-600/30 rounded hover:bg-violet-900/20 transition-colors"
        data-testid="create-new-treatment-btn">
        + Create New Treatment
      </button>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main Surface Treatment Section                                      */
/* ------------------------------------------------------------------ */
export const SurfaceTreatmentSection = ({ line, bomId, revisionId, onRefresh, isDraft = true, onBusy }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  if (!line || line.make_buy !== 'Make' || line.is_assembly) return null;

  const allRules = line?.applied_process_rules || [];
  const activeRules = allRules.filter(r => r.is_selected);
  const treatmentCost = activeRules.reduce((sum, r) => sum + (r.cost || 0), 0);
  // Use rule_code for filtering against process_code from library
  const activeRuleCodes = new Set(activeRules.map(r => r.rule_code).filter(Boolean));

  return (
    <div className="border border-violet-700/30 rounded-lg p-3 bg-violet-900/10" data-testid="treatment-section">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setCollapsed(!collapsed)}>
        <div className="text-[10px] text-violet-400 font-semibold uppercase tracking-wider flex items-center gap-1">
          <Droplets className="w-3 h-3" /> Surface Preparation & Treatment <span className="text-amber-600/60">[8.5.1]</span>
          {activeRules.length > 0 && (
            <span className="text-violet-300/60 ml-1">({activeRules.length})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {treatmentCost > 0 && <span className="font-mono text-emerald-400 text-[10px] font-semibold">{treatmentCost.toLocaleString()}</span>}
          {collapsed ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronUp className="w-3 h-3 text-slate-500" />}
        </div>
      </div>

      {!collapsed && (
        <div className="mt-2 space-y-1">
          {activeRules.length === 0 ? (
            <div className="text-[10px] text-slate-500 py-2 text-center border border-dashed border-slate-700/40 rounded">
              No surface treatments applied. Add treatments this part requires.
            </div>
          ) : (
            <>
              {activeRules.map((rule, idx) => {
                const actualIdx = allRules.findIndex(r => r.rule_id === rule.rule_id && r.is_selected);
                return (
                  <TreatmentRow key={`${rule.rule_id}-${idx}`} rule={rule} idx={actualIdx}
                    bomId={bomId} revisionId={revisionId} lineId={line.line_id}
                    onRefresh={onRefresh} isDraft={isDraft} />
                );
              })}
              <div className="border-t border-violet-600/20 mt-2 pt-1.5 flex justify-between items-center">
                <span className="text-[10px] text-violet-300 font-semibold">Treatment Cost:</span>
                <span className="font-mono text-violet-300 text-[11px] font-bold" data-testid="treatment-total">
                  {treatmentCost.toLocaleString()}
                </span>
              </div>
            </>
          )}

          {isDraft && !showAdd && (
            <Button size="sm" variant="outline" onClick={() => setShowAdd(true)}
              className="w-full h-6 text-[10px] border-violet-600/30 text-violet-400 hover:bg-violet-900/20 hover:text-violet-300 mt-1"
              data-testid="treatment-add-btn">
              <Plus className="w-3 h-3 mr-1" /> Add Treatment
            </Button>
          )}

          {showAdd && (
            <AddTreatmentPopover bomId={bomId} revisionId={revisionId} lineId={line.line_id}
              activeRuleCodes={activeRuleCodes}
              onRefresh={onRefresh} onClose={() => setShowAdd(false)} onBusy={onBusy} />
          )}
        </div>
      )}
    </div>
  );
};
